import { useTranslation } from '@/i18n/useTranslation';

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { reviewsApi } from '@/api';
import { useAuth } from '@/auth/useAuth';
import { EmptyState } from '@/components/common/EmptyState';
import { InlineError } from '@/components/common/InlineError';
import { Pagination } from '@/components/common/Pagination';
import type { Prediction, ReviewCategory, ReviewEvent, ReviewSubmit, ScanDetail } from '@/types';

const reasons = [
  ['wrong_category', 'Wrong category'],
  ['false_positive', 'No defect (false positive)'],
  ['inaccurate_box', 'Incorrect box / location'],
  ['duplicate', 'Duplicate detection'],
] as const;

function DetectionOverlay({
  predictions,
  width,
  height,
  labelled = false,
}: {
  predictions: Prediction[];
  width: number;
  height: number;
  labelled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-label={t('Detection boxes')}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    >
      {predictions.map((prediction, index) =>
        prediction.bbox && prediction.bbox.length === 4 ? (
          <g key={index}>
            <rect
              x={prediction.bbox[0]}
              y={prediction.bbox[1]}
              width={prediction.bbox[2]! - prediction.bbox[0]!}
              height={prediction.bbox[3]! - prediction.bbox[1]!}
              fill="none"
              stroke="#ffcc00"
              strokeWidth={labelled ? 3 : 2}
              vectorEffect={labelled ? undefined : 'non-scaling-stroke'}
            />
            {labelled && (
              <text
                x={prediction.bbox[0]}
                y={Math.max(16, prediction.bbox[1]! - 4)}
                fill="#ffcc00"
                stroke="#000"
                strokeWidth="2"
                paintOrder="stroke"
              >
                #{index + 1}
              </text>
            )}
          </g>
        ) : null,
      )}
    </svg>
  );
}

function ReviewForm({
  scanId,
  target,
  prior,
  categories,
  missed = false,
  onCancel,
  onSaved,
}: {
  scanId: string;
  target: string;
  prior?: ReviewEvent;
  categories: ReviewCategory[];
  missed?: boolean;
  onCancel?: () => void;
  onSaved?: () => void;
}) {
  const { displayValue, t } = useTranslation();
  const cache = useQueryClient();
  const [assessment, setAssessment] = useState<'correct' | 'incorrect'>(
    missed ? 'incorrect' : (prior?.assessment ?? 'correct'),
  );
  const [reason, setReason] = useState<string>(missed ? 'missed_defect' : (prior?.reason ?? ''));
  const [category, setCategory] = useState(
    categories.find(
      (item) => item.id === prior?.corrected_category?.id && item.is_active && item.group_is_active,
    )?.id ?? '',
  );
  const [note, setNote] = useState(prior?.note ?? '');
  const mutation = useMutation({
    mutationFn: (payload: ReviewSubmit) => reviewsApi.submitReview(scanId, payload),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['scan-reviews', scanId] });
      void cache.invalidateQueries({ queryKey: ['scans'] });
      onSaved?.();
    },
  });
  const chosenReason = missed ? 'missed_defect' : reason;
  const showsCategory =
    assessment === 'incorrect' && !!chosenReason && chosenReason !== 'false_positive';
  const needsCategory = showsCategory && ['wrong_category', 'missed_defect'].includes(chosenReason);
  const active = categories.filter((item) => item.is_active && item.group_is_active);
  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        const payload: ReviewSubmit = {
          submission_id: crypto.randomUUID(),
          target_key: target,
          assessment,
        };
        if (assessment === 'incorrect') {
          payload.reason = chosenReason as ReviewSubmit['reason'];
          if (showsCategory && category) payload.corrected_category_id = category;
        }
        if (note.trim()) payload.note = note.trim();
        mutation.mutate(payload);
      }}
    >
      {!missed && (
        <label className="field">
          <span className="field__label">{t('Detection assessment')}</span>
          <select
            className="select"
            value={assessment}
            onChange={(e) => {
              setAssessment(e.target.value as 'correct' | 'incorrect');
              setReason('');
              setCategory('');
            }}
          >
            <option value="correct">{t('Correct')}</option>
            <option value="incorrect">{t('Incorrect')}</option>
          </select>
        </label>
      )}
      {assessment === 'incorrect' && !missed && (
        <label className="field">
          <span className="field__label">{t('Reason')}</span>
          <select
            className="select"
            required
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setCategory('');
            }}
          >
            <option value="">{t('Select reason…')}</option>
            {reasons.map(([value, label]) => (
              <option value={value} key={value}>
                {t(label)}
              </option>
            ))}
          </select>
        </label>
      )}
      {showsCategory && (
        <label className="field">
          <span className="field__label">
            {needsCategory ? t('Correct defect category') : t('Correct defect category (optional)')}
          </span>
          <select
            className="select"
            required={needsCategory}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">{t('Select category…')}</option>
            {active.map((item) => (
              <option value={item.id} key={item.id}>
                {displayValue(item.group_code)} · {displayValue(item.name)}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="field">
        <span className="field__label">{t('Note (optional)')}</span>
        <input
          className="input"
          maxLength={1000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <div className="cluster">
        <button
          type="submit"
          className="btn btn--primary btn--sm"
          disabled={
            mutation.isPending ||
            (needsCategory && !category) ||
            (assessment === 'incorrect' && !chosenReason)
          }
        >
          {mutation.isPending ? t('Saving…') : prior ? t('Save revision') : t('Save review')}
        </button>
        {onCancel && (
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={onCancel}
            disabled={mutation.isPending}
          >
            {t('Cancel missed defect')}
          </button>
        )}
      </div>
      {mutation.isSuccess && <p role="status">{t('Review saved.')}</p>}
      {mutation.isError && <InlineError error={mutation.error} />}
    </form>
  );
}

function ScanReview({ scan, userId }: { scan: ScanDetail; userId: string }) {
  const { displayValue, locale, t } = useTranslation();
  const reviews = useQuery({
    queryKey: ['scan-reviews', scan.inference_id],
    queryFn: () => reviewsApi.getReviews(scan.inference_id),
  });
  const categories = useQuery({
    queryKey: ['review-categories'],
    queryFn: reviewsApi.listCategories,
  });
  const [missedKeys, setMissedKeys] = useState<string[]>([]);
  const predictions = scan.result?.predictions ?? [];
  const current = reviews.data?.current ?? [];
  const mine = (target: string) =>
    current.find((event) => event.target_key === target && event.author_user_id === userId);
  return (
    <div className="stack" style={{ gap: 'var(--space-4)' }}>
      <h2>{t('Scan {id}', { id: scan.inference_id })}</h2>
      <p>{t('Inspected by {username}', { username: scan.owner_username ?? '—' })}</p>
      <p>
        {scan.completed_at ? new Date(scan.completed_at).toLocaleString(locale) : t('Pending')} ·{' '}
        {scan.model_version ?? t('Unknown model')} ·{' '}
        {t('{count} detections', { count: predictions.length })}
      </p>
      {scan.download_url && (
        <div style={{ position: 'relative', width: '100%', maxWidth: 900 }}>
          <img
            src={scan.download_url}
            alt={t('Saved scan for review')}
            style={{ display: 'block', width: '100%' }}
          />
          {scan.width && scan.height && (
            <DetectionOverlay predictions={predictions} width={scan.width} height={scan.height} labelled />
          )}
        </div>
      )}
      {reviews.isError && <InlineError error={reviews.error} />}
      {categories.isError && <InlineError error={categories.error} />}
      {predictions.map((prediction, index) => {
        const target = `prediction:${index}`;
        const other = current.filter(
          (event) => event.target_key === target && event.author_user_id !== userId,
        );
        return (
          <section key={target} className="glass-panel glass-panel--tight stack">
            <h3>
              {t('Detection #{number}', { number: index + 1 })} ·{' '}
              {prediction.label
                ? displayValue(prediction.label)
                : t('Class {id}', { id: prediction.class_id })}{' '}
              · {(prediction.confidence * 100).toFixed(1)}%
            </h3>
            {mine(target) && (
              <p>
                {t('Your review:')} {displayValue(mine(target)!.assessment)}
                {mine(target)!.reason ? ` · ${displayValue(mine(target)!.reason!)}` : ''}
                {mine(target)!.corrected_category
                  ? ` → ${displayValue(mine(target)!.corrected_category!.name)}`
                  : ''}
              </p>
            )}
            {other.map((event) => (
              <p key={event.id}>
                {t('Other reviewer:')} {displayValue(event.assessment)}
                {event.reason ? ` · ${displayValue(event.reason)}` : ''}
              </p>
            ))}
            {categories.data && (
              <ReviewForm
                key={`${target}:${mine(target)?.id ?? ''}`}
                scanId={scan.inference_id}
                target={target}
                prior={mine(target)}
                categories={categories.data}
              />
            )}
          </section>
        );
      })}
      <section className="glass-panel glass-panel--tight stack">
        <h3>{t('Missed defects')}</h3>
        <p>{t('Add a separate entry for each defect the model did not detect.')}</p>
        {current
          .filter((event) => event.target_key.startsWith('missed:'))
          .map((event) => (
            <div key={event.id} className="stack">
              <p>
                {event.author_user_id === userId
                  ? t('Your missed defect:')
                  : t('Other reviewer missed defect:')}{' '}
                {event.corrected_category
                  ? displayValue(event.corrected_category.name)
                  : t('Unknown category')}
              </p>
              {event.author_user_id === userId && categories.data && (
                <ReviewForm
                  key={event.id}
                  scanId={scan.inference_id}
                  target={event.target_key}
                  prior={event}
                  categories={categories.data}
                  missed
                />
              )}
            </div>
          ))}
        {missedKeys
          .filter((target) => !mine(target))
          .map(
            (target) =>
              categories.data && (
                <ReviewForm
                  key={target}
                  scanId={scan.inference_id}
                  target={target}
                  categories={categories.data}
                  missed
                  onCancel={() => setMissedKeys((keys) => keys.filter((key) => key !== target))}
                  onSaved={() => setMissedKeys((keys) => keys.filter((key) => key !== target))}
                />
              ),
          )}
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => setMissedKeys((keys) => [...keys, `missed:${crypto.randomUUID()}`])}
        >
          {t('Add missed defect')}
        </button>
      </section>
      {reviews.data && reviews.data.history.length > 0 && (
        <details>
          <summary>{t('Review history ({count})', { count: reviews.data.history.length })}</summary>
          <ul>
            {reviews.data.history.map((event) => (
              <li key={event.id}>
                #{event.revision} · {event.target_key} · {displayValue(event.assessment)}
                {event.reason ? ` · ${displayValue(event.reason)}` : ''} ·{' '}
                {t('Reviewed by {username}', { username: event.author_username ?? '—' })} ·{' '}
                {new Date(event.created_at).toLocaleString(locale)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function ScanReviewDialog({
  scanId,
  admin,
  userId,
  onClose,
}: {
  scanId: string;
  admin: boolean;
  userId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const detail = useQuery({
    queryKey: ['scan-detail', admin, scanId],
    queryFn: () => reviewsApi.getScan(scanId, admin),
  });

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    closeButtonRef.current?.focus();
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className="scan-review-dialog"
      aria-modal="true"
      aria-labelledby="scan-review-dialog-title"
      onClose={onClose}
    >
      <div className="flex-between">
        <h2 id="scan-review-dialog-title">{t('Scan review')}</h2>
        <button
          ref={closeButtonRef}
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => dialogRef.current?.close()}
        >
          {t('Close scan')}
        </button>
      </div>
      {detail.isLoading && <p>{t('Loading scan…')}</p>}
      {detail.isError && <InlineError error={detail.error} onRetry={() => detail.refetch()} />}
      {detail.data && <ScanReview scan={detail.data} userId={userId} />}
    </dialog>
  );
}

const THUMBNAIL_MAX_HEIGHT = 384;

function ScanThumbnail({
  url,
  scanId,
  width,
  height,
  predictions,
  onReview,
}: {
  url: string | null | undefined;
  scanId: string;
  width?: number | null;
  height?: number | null;
  predictions?: Prediction[];
  onReview: () => void;
}) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const sized = Boolean(width && height);
  // Cap the frame so its height never exceeds the maximum while keeping the image aspect ratio.
  const frameStyle = sized
    ? {
        aspectRatio: `${width} / ${height}`,
        width: `min(100%, ${Math.round((THUMBNAIL_MAX_HEIGHT * width!) / height!)}px)`,
      }
    : { aspectRatio: '4 / 3', width: '100%' };

  return (
    <button
      type="button"
      className="scan-thumbnail"
      aria-label={t('Review scan {id}', { id: scanId })}
      style={frameStyle}
      onClick={onReview}
    >
      {url && !failed ? (
        <>
          <img
            src={url}
            alt={t('Saved scan thumbnail')}
            width={width || 640}
            height={height || 480}
            loading="lazy"
            decoding="async"
            onError={() => setFailed(true)}
          />
          {sized && predictions && predictions.length > 0 && (
            <DetectionOverlay predictions={predictions} width={width!} height={height!} />
          )}
        </>
      ) : (
        <span className="scan-thumbnail__unavailable">{t('Image unavailable')}</span>
      )}
    </button>
  );
}


export function LogsPage() {
  const { locale, t } = useTranslation();
  const { role, currentUser } = useAuth();
  const admin = role === 'admin' || role === 'super_admin';
  const [page, setPage] = useState(1);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const invalidRange = Boolean(from && to && from > to);
  const scans = useQuery({
    queryKey: ['scans', admin, page, from, to],
    queryFn: () =>
      reviewsApi.listScans(
        admin,
        page,
        from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
        to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
      ),
    enabled: !invalidRange,
  });
  return (
    <div className="stack" style={{ gap: 'var(--space-5)' }}>
      <header>
        <h1>{t('Inspections · Scan review')}</h1>
        <p>
          {t(
            'Each saved inference is an inspection. Review its image and correct individual detections.',
          )}
        </p>
        <p>
          {t('Use the thumbnails to find scans that need review. Select an image to review it.')}
        </p>
      </header>
      <div className="cluster">
        <label className="field">
          {t('From')}
          <input
            type="date"
            className="input"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
          />
        </label>
        <label className="field">
          {t('To')}
          <input
            type="date"
            className="input"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
          />
        </label>
      </div>
      {invalidRange && (
        <p role="alert">{t('The start date must not be later than the end date.')}</p>
      )}
      {scans.isError && <InlineError error={scans.error} onRetry={() => scans.refetch()} />}
      {scans.isLoading && <p>{t('Loading scans…')}</p>}
      {scans.data?.items.length === 0 && <EmptyState title={t('No saved scans in this range')} />}
      {scans.data && scans.data.items.length > 0 && (
        <ul className="scan-grid">
          {scans.data.items.map((scan) => (
            <li
              key={scan.inference_id}
              className={`scan-card${scan.detection_count > 0 ? ' scan-card--flagged' : ''}`}
            >
              <div className="scan-card__media">
                <ScanThumbnail
                  key={scan.thumbnail_url}
                  url={scan.thumbnail_url}
                  scanId={scan.inference_id}
                  width={scan.width}
                  height={scan.height}
                  predictions={scan.predictions}
                  onReview={() => setSelected(scan.inference_id)}
                />
                {scan.detection_count > 0 && (
                  <span className="scan-card__badge" aria-hidden="true">
                    {t('Detections')}: {scan.detection_count}
                  </span>
                )}
              </div>
              <div className="scan-card__footer">
                <dl className="scan-details">
                  <div>
                    <dt>{t('Saved')}:</dt>
                    <dd>
                      {scan.completed_at ? new Date(scan.completed_at).toLocaleString(locale) : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt>{t('Inspected by')}:</dt>
                    <dd>{scan.owner_username ?? '—'}</dd>
                  </div>
                  <div>
                    <dt>{t('Detections')}:</dt>
                    <dd>{scan.detection_count}</dd>
                  </div>
                  <div>
                    <dt>{t('Reviews')}:</dt>
                    <dd>{scan.review_count}</dd>
                  </div>
                </dl>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => setSelected(scan.inference_id)}
                >
                  {t('Review')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {scans.data && (
        <Pagination
          page={page}
          pageSize={20}
          total={scans.data.total}
          onPageChange={(next) => {
            setPage(next);
            setSelected(null);
          }}
        />
      )}
      {selected && currentUser && (
        <ScanReviewDialog
          scanId={selected}
          admin={admin}
          userId={currentUser.id}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
